import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import PropTypes from 'prop-types';
import { axiosGet } from '../utils/axiosGet';
import { chairFinalReportStatus } from '../utils/chairFinalReportStatus';
import { devError } from '../utils/devLog';

const ChairReportStatusesContext = createContext();

const INITIAL_RETRY_DELAY_MS = 1000;

export const ChairReportStatusesProvider = ({ children }) => {
  const [reportStatuses, setReportStatuses] = useState({});
  const [allReportsSubmitted, setAllReportsSubmitted] = useState(false);
  const [statusesLoading, setStatusesLoading] = useState(true);
  const hasLoadedOnceRef = useRef(false);

  const fetchStatuses = useCallback(async ({ isRetry = false } = {}) => {
    try {
      setStatusesLoading(true);
      const response = await axiosGet('chair-reports');
      const reports = response?.data?.data || [];

      const statuses = {};
      reports.forEach((report) => {
        const attrs = report.attributes;
        const committeeId = Number(attrs.sectionalCommitteeId);

        statuses[committeeId] = chairFinalReportStatus({
          reportId: report.id,
          status: attrs.status || 'draft',
          formData: {
            gapAnalysis: attrs.gapAnalysis || '',
            evaluationProcess: attrs.evaluationProcess || '',
            integrityMatters: attrs.integrityMatters || '',
            diversityIssues: attrs.diversityIssues || '',
            fnsFeedback: attrs.fnsFeedback || '',
            generalObservations: attrs.generalObservations || '',
            candidateReports: attrs.candidateReports || [],
            memberAttendance: attrs.memberAttendance || [],
          },
        });
      });

      setReportStatuses(statuses);
      setAllReportsSubmitted(
        reports.length > 0 &&
          reports.every((r) => r.attributes?.status === 'submitted'),
      );
      hasLoadedOnceRef.current = true;
      setStatusesLoading(false);
    } catch (e) {
      devError(
        '[ChairReportStatuses] Failed to fetch chair report statuses:',
        e?.message || e,
      );
      if (!hasLoadedOnceRef.current && !isRetry) {
        setTimeout(() => {
          fetchStatuses({ isRetry: true });
        }, INITIAL_RETRY_DELAY_MS);
        return;
      }
      setStatusesLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStatuses();
  }, [fetchStatuses]);

  const value = useMemo(
    () => ({
      reportStatuses,
      allReportsSubmitted,
      statusesLoading,
      refetchStatuses: fetchStatuses,
    }),
    [reportStatuses, allReportsSubmitted, statusesLoading, fetchStatuses],
  );

  return (
    <ChairReportStatusesContext.Provider value={value}>
      {children}
    </ChairReportStatusesContext.Provider>
  );
};

ChairReportStatusesProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export const useChairReportStatuses = () =>
  useContext(ChairReportStatusesContext);
